import { useEffect, useId, useState } from 'react';
import { GRID_SIZES, type Language } from '@shared/constants';
import type { Settings } from '@shared/schemas';
import { useT } from '../i18n';
import { getVoices, localeFor, onVoicesChanged, speak, speechSupported } from '../lib/speech';
import { Dialog } from './Dialog';
import { Notice } from './Notice';

interface Props {
  open: boolean;
  settings: Settings;
  onClose(): void;
  onSave(settings: Settings): void;
}

export function SettingsDialog({ open, settings, onClose, onSave }: Props) {
  const t = useT();
  const ids = { gender: useId(), voice: useId(), speed: useId(), grid: useId(), lang: useId() };
  const [draft, setDraft] = useState(settings);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    if (open) setDraft(settings);
  }, [open, settings]);

  useEffect(() => {
    const load = () => setVoices(getVoices());
    load();
    return onVoicesChanged(load);
  }, []);

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const base = localeFor(draft.language).split('-')[0]!;
  const languageVoices = voices.filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith(base));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('settingsTitle')}
      wide
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="btn btn--primary" onClick={() => onSave(draft)}>
            {t('save')}
          </button>
        </>
      }
    >
      <div className="form">
        <h3>{t('voiceSection')}</h3>
        {!speechSupported() && <Notice kind="warning">{t('voiceUnavailable')}</Notice>}
        <label className="check">
          <input type="checkbox" checked={draft.speakOnTap} onChange={(e) => set('speakOnTap', e.target.checked)} />
          <span>
            <strong>{t('speakOnTap')}</strong>
            <span className="muted small">{t('speakOnTapHint')}</span>
          </span>
        </label>
        <div className="field">
          <label htmlFor={ids.gender}>{t('voiceGender')}</label>
          <select id={ids.gender} value={draft.voiceGender} onChange={(e) => set('voiceGender', e.target.value as Settings['voiceGender'])}>
            <option value="female">{t('female')}</option>
            <option value="male">{t('male')}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor={ids.voice}>{t('voiceChoice')}</label>
          <select id={ids.voice} value={draft.voiceURI ?? ''} onChange={(e) => set('voiceURI', e.target.value || null)}>
            <option value="">{t('voiceAuto')}</option>
            {languageVoices.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>
                {v.name} ({v.lang})
              </option>
            ))}
          </select>
          {speechSupported() && voices.length > 0 && languageVoices.length === 0 && <p className="muted">{t('noVoices')}</p>}
        </div>
        <div className="field">
          <label htmlFor={ids.speed}>
            {t('voiceSpeed')}: <strong>{draft.voiceSpeed.toFixed(1)}×</strong>
          </label>
          <input
            id={ids.speed}
            type="range"
            min={0.5}
            max={2}
            step={0.1}
            value={draft.voiceSpeed}
            onChange={(e) => set('voiceSpeed', Number(e.target.value))}
          />
        </div>
        <button type="button" className="btn btn--secondary" onClick={() => speak(t('testPhrase'), draft)} disabled={!speechSupported()}>
          🔊 {t('testVoice')}
        </button>

        <h3>{t('displaySection')}</h3>
        <div className="field">
          <label htmlFor={ids.grid}>{t('gridSize')}</label>
          <select id={ids.grid} value={draft.gridSize} onChange={(e) => set('gridSize', Number(e.target.value) as Settings['gridSize'])}>
            {GRID_SIZES.map((n) => (
              <option key={n} value={n}>
                {t('columns', { n })}
              </option>
            ))}
          </select>
          <p className="muted">{t('gridHint')}</p>
        </div>
        <div className="field">
          <label htmlFor={ids.lang}>{t('language')}</label>
          <select id={ids.lang} value={draft.language} onChange={(e) => set('language', e.target.value as Language)}>
            <option value="pt">Português (Brasil)</option>
            <option value="en">English</option>
          </select>
        </div>
        <p className="muted small">{t('syncNote')}</p>
        <p className="attribution small">{t('attribution')}</p>
      </div>
    </Dialog>
  );
}
